import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';

export const CDR_ENTRY_BINDING_CONTENT_TYPE = 'cdrEntryBinding';
export const CDR_ENTRY_BINDING_STYLES = Object.freeze({
  BOLD: 'bold',
  UNDERLINE: 'underline',
  ITALIC: 'italic'
});

const STYLE_RENDERERS = {
  [CDR_ENTRY_BINDING_STYLES.BOLD]: children => <b>{children}</b>,
  [CDR_ENTRY_BINDING_STYLES.UNDERLINE]: children => <u>{children}</u>,
  [CDR_ENTRY_BINDING_STYLES.ITALIC]: children => <i>{children}</i>
};

const STYLE_RENDER_ORDER = [
  CDR_ENTRY_BINDING_STYLES.BOLD,
  CDR_ENTRY_BINDING_STYLES.UNDERLINE,
  CDR_ENTRY_BINDING_STYLES.ITALIC
];

const AMOUNT_TOKEN_KEYS = new Set(['amount', 'additionalValue']);
const CDR_AMOUNT_PATTERN = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;

const getContentTypeId = entry =>
  entry?.contentType?.sys?.id || entry?.sys?.contentType?.sys?.id;

export const isCdrEntryBinding = entry =>
  getContentTypeId(entry) === CDR_ENTRY_BINDING_CONTENT_TYPE;

const getPathValue = (data, path) => {
  if (!data || !path) return undefined;

  return path.split('.').reduce((current, key) => {
    if (current === undefined || current === null) return undefined;
    return current?.fields?.[key] ?? current?.[key];
  }, data);
};

const shouldFormatCdrAmount = binding => {
  const tokenKey = binding?.tokenKey || binding?.fields?.tokenKey;
  const fieldName = tokenKey?.split('.').pop();

  return AMOUNT_TOKEN_KEYS.has(fieldName);
};

const formatCdrAmount = value => {
  const text = String(value);
  const match = text.match(CDR_AMOUNT_PATTERN);

  if (!match) return text;

  const [, sign, wholeValue, decimalValue] = match;
  const normalizedWhole = wholeValue.replace(/^0+(?=\d)/, '');
  const formattedWhole = normalizedWhole.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ','
  );

  if (!decimalValue || /^0+$/.test(decimalValue)) {
    return `${sign}${formattedWhole}`;
  }

  return `${sign}${formattedWhole}.${decimalValue.padEnd(2, '0')}`;
};

const formatCdrPrimitiveValue = (value, binding) => {
  const text = String(value);

  return shouldFormatCdrAmount(binding) ? formatCdrAmount(text) : text;
};

const renderValue = (value, binding) => {
  if (value === undefined || value === null) return null;

  if (typeof value === 'string' || typeof value === 'number') {
    return formatCdrPrimitiveValue(value, binding);
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <React.Fragment key={item?.id || item?.sys?.id || index}>
        {index > 0 ? ', ' : null}
        {renderValue(item, binding)}
      </React.Fragment>
    ));
  }

  if (value.nodeType === 'document') {
    return documentToReactComponents(value, getCdrEntryBindingRichTextOptions());
  }

  if (value.fields) {
    return renderValue(value.fields, binding);
  }

  const fallback =
    value.title ||
    value.name ||
    value.label ||
    value.text ||
    value.url ||
    value.uri ||
    value.file?.url;

  return fallback ? formatCdrPrimitiveValue(fallback, binding) : null;
};

const getBindingStyles = binding => {
  const styles = binding?.style || binding?.fields?.style;
  return Array.isArray(styles) ? styles : [];
};

const applyBindingStyles = (value, binding) =>
  STYLE_RENDER_ORDER.reduce((result, style) => {
    if (!getBindingStyles(binding).includes(style)) return result;
    return STYLE_RENDERERS[style](result);
  }, value);

const richTextToPlainText = document =>
  document?.content
    ?.map(node => {
      if (node.nodeType === 'text') return node.value;
      return richTextToPlainText(node);
    })
    .join('') || '';

const valueToText = (value, binding) => {
  if (value === undefined || value === null) return null;

  if (typeof value === 'string' || typeof value === 'number') {
    return formatCdrPrimitiveValue(value, binding);
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (Array.isArray(value)) {
    return value
      .map(item => valueToText(item, binding))
      .filter(Boolean)
      .join(', ');
  }

  if (value.nodeType === 'document') {
    return richTextToPlainText(value);
  }

  if (value.fields) {
    return valueToText(value.fields, binding);
  }

  const fallback =
    value.title ||
    value.name ||
    value.label ||
    value.text ||
    value.url ||
    value.uri ||
    value.file?.url;

  return fallback ? formatCdrPrimitiveValue(fallback, binding) : null;
};

export const resolveCdrEntryBinding = binding => {
  if (!isCdrEntryBinding(binding)) return null;

  const resolvedValue = renderValue(
    getPathValue(binding.cdrEntry, binding.tokenKey),
    binding
  );
  const value = resolvedValue || binding.placeholder || null;

  return value === null ? null : applyBindingStyles(value, binding);
};

export const renderCdrEntryBinding = node =>
  resolveCdrEntryBinding(node?.data?.target);

export const resolveCdrEntryBindingText = binding => {
  if (!isCdrEntryBinding(binding)) return null;

  const resolvedValue = valueToText(
    getPathValue(binding.cdrEntry, binding.tokenKey),
    binding
  );

  return resolvedValue || binding.placeholder || null;
};

export const replaceCdrEntryBindingTokens = (text, bindings) => {
  if (typeof text !== 'string' || !Array.isArray(bindings)) return text;

  return bindings.reduce((result, binding) => {
    if (!binding?.placeholder) return result;

    const resolvedValue = resolveCdrEntryBindingText(binding);
    if (resolvedValue === null) return result;

    return result.split(binding.placeholder).join(resolvedValue);
  }, text);
};

const isRichTextDocument = value => value?.nodeType === 'document';

export const replaceCdrEntryBindingTokensInContentfulData = (
  data,
  activeBindings
) => {
  if (typeof data === 'string') {
    return replaceCdrEntryBindingTokens(data, activeBindings);
  }

  if (Array.isArray(data)) {
    return data.map(item =>
      replaceCdrEntryBindingTokensInContentfulData(item, activeBindings)
    );
  }

  if (typeof data !== 'object' || data === null) {
    return data;
  }

  if (isRichTextDocument(data) || isCdrEntryBinding(data)) {
    return data;
  }

  const nextBindings = Array.isArray(data.cdrEntryBindings)
    ? data.cdrEntryBindings
    : activeBindings;

  return Object.keys(data).reduce((result, key) => {
    result[key] =
      key === 'cdrEntryBindings'
        ? data[key]
        : replaceCdrEntryBindingTokensInContentfulData(
            data[key],
            nextBindings
          );

    return result;
  }, {});
};

export const getCdrEntryBindingRichTextOptions = config => ({
  ...config,
  renderNode: {
    ...config?.renderNode,
    [INLINES.EMBEDDED_ENTRY]: (node, children) => {
      const renderedBinding = renderCdrEntryBinding(node);
      if (renderedBinding !== null) return renderedBinding;

      const customEmbeddedEntryRenderer =
        config?.renderNode?.[INLINES.EMBEDDED_ENTRY];

      return customEmbeddedEntryRenderer
        ? customEmbeddedEntryRenderer(node, children)
        : null;
    }
  }
});
